import React, { useState, useEffect } from 'react';
import { Plus, Package, AlertTriangle, ArrowUp, ArrowDown, Trash2 } from 'lucide-react';
import api from '../utils/api';
import { formatDateBR } from '../utils/formatDate';
import toast from 'react-hot-toast';

const TIPO_CORES = { vacina:'#2563EB', vermifugo:'#16A34A', antibiotico:'#EA580C', antiparasitario:'#7C3AED', suplemento:'#0891B2', racao:'#D97706', material:'#6B7280', materia_prima:'#059669', outro:'#6B7280' };
const TIPOS_PRODUTO = ['vacina','vermifugo','antibiotico','antiparasitario','suplemento','racao','material','materia_prima','outro'];
const TIPO_LABELS = { vacina:'Vacina', vermifugo:'Vermífugo', antibiotico:'Antibiótico', antiparasitario:'Antiparasitário', suplemento:'Suplemento', racao:'Ração', material:'Material', materia_prima:'Matéria-Prima', outro:'Outro' };
const labelTipo = (t) => TIPO_LABELS[t] || t;
const UNIDADES = ['ml','L','mg','g','kg','comprimido','dose','saco','unidade'];

// Formata número: 1001.0 → 1001 | 1.5 → 1,5
const fmt = (v) => {
  if (v == null || v === '') return '0';
  const n = parseFloat(v);
  if (isNaN(n)) return v;
  return n % 1 === 0 ? n.toFixed(0) : n.toFixed(1).replace('.', ',');
};

export default function Estoque() {
  const [produtos, setProdutos] = useState([]);
  const [movs, setMovs] = useState([]);
  const [tab, setTab] = useState('produtos');
  const [showProduto, setShowProduto] = useState(false);
  const [showMovimento, setShowMovimento] = useState(false);
  const [editando, setEditando] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formProd, setFormProd] = useState({ nome:'', tipo:'vacina', principio_ativo:'', fabricante:'', unidade:'ml', estoque_minimo:0, periodo_carencia_dias:0 });
  const [formMov, setFormMov] = useState({ produto_id:'', tipo:'entrada', quantidade:'', lote_produto:'', validade:'', fornecedor:'', nota_fiscal:'', custo_unitario:'', motivo:'' });
  const [filtroTipo, setFiltroTipo] = useState('');

  const loadProdutos = () => api.get('/estoque/produtos').then(r => { setProdutos(r.data); setLoading(false); });
  const loadMovs = () => api.get('/estoque/movimentacoes').then(r => setMovs(r.data));
  useEffect(() => { loadProdutos(); loadMovs(); }, []);

  const salvarProduto = async () => {
    try {
      if (editando) await api.put(`/estoque/produtos/${editando.id}`, formProd);
      else await api.post('/estoque/produtos', formProd);
      toast.success(editando ? 'Atualizado!' : 'Cadastrado!');
      setShowProduto(false); setEditando(null); loadProdutos();
    } catch (e) { toast.error(e.response?.data?.error || 'Erro'); }
  };

  const excluirProduto = async (id, nome) => {
    if (!window.confirm(`Excluir "${nome}"?`)) return;
    try {
      await api.delete(`/estoque/produtos/${id}`);
      toast.success('Excluído');
      loadProdutos();
    } catch (e) {
      const err = e.response?.data;
      if (err?.pode_inativar) {
        const c = err.contagens || {};
        const linhas = [];
        if (c.movimentacoes) linhas.push(`• ${c.movimentacoes} movimentação(ões) de estoque`);
        if (c.intervencoes) linhas.push(`• ${c.intervencoes} intervenção(ões) sanitária(s)`);
        if (c.ingredientes_receita) linhas.push(`• ${c.ingredientes_receita} receita(s) de ração`);
        if (c.itens_producao) linhas.push(`• ${c.itens_producao} produção(ões) de ração`);
        const detalhe = linhas.length ? `\n\nRegistros que serão excluídos:\n${linhas.join('\n')}` : '';
        const opcao = window.confirm(
          `"${nome}" possui registros vinculados.${detalhe}\n\nClique OK para EXCLUIR tudo permanentemente, ou Cancelar para Inativar (preserva histórico).`
        );
        if (opcao) {
          if (!window.confirm(`Confirmar exclusão definitiva de "${nome}" e todos os seus registros? Esta ação não pode ser desfeita.`)) return;
          try {
            await api.delete(`/estoque/produtos/${id}?force=true`);
            toast.success('Produto e registros excluídos');
            loadProdutos();
          } catch { toast.error('Erro ao excluir produto'); }
        } else {
          try {
            await api.patch(`/estoque/produtos/${id}/inativar`);
            toast.success('Produto inativado');
            loadProdutos();
          } catch { toast.error('Erro ao inativar produto'); }
        }
      } else {
        toast.error(err?.error || 'Erro ao excluir produto');
      }
    }
  };

  const salvarMovimento = async () => {
    try {
      await api.post('/estoque/movimentacoes', formMov);
      toast.success('Movimentação registrada!');
      setShowMovimento(false); setFormMov(p => ({ ...p, quantidade:'', lote_produto:'', validade:'', fornecedor:'' }));
      loadProdutos(); loadMovs();
    } catch (e) { toast.error(e.response?.data?.error || 'Erro'); }
  };

  const abrir = (p) => {
    setEditando(p);
    setFormProd({ nome:p.nome, tipo:p.tipo, principio_ativo:p.principio_ativo||'', fabricante:p.fabricante||'', unidade:p.unidade||'ml', estoque_minimo:p.estoque_minimo, periodo_carencia_dias:p.periodo_carencia_dias });
    setShowProduto(true);
  };

  const criticos = produtos.filter(p => (p.estoque_atual||0) <= (p.estoque_minimo||0));

  const produtosFiltrados = filtroTipo ? produtos.filter(p => p.tipo === filtroTipo) : produtos;
  const gruposPorTipo = () => {
    const porTipo = {};
    produtosFiltrados.forEach(p => { (porTipo[p.tipo] = porTipo[p.tipo] || []).push(p); });
    const tiposPresentes = Object.keys(porTipo);
    const ordem = [...TIPOS_PRODUTO, ...tiposPresentes.filter(t => !TIPOS_PRODUTO.includes(t))];
    return ordem.filter(t => porTipo[t]?.length).map(t => ({ tipo: t, itens: porTipo[t] }));
  };

  return (
    <div>
      <div className="page-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <div><h1>Estoque & Insumos</h1><p>{produtos.length} produtos · {criticos.length} críticos</p></div>
        <div style={{ display:'flex', gap:8 }}>
          <button className="btn btn-secondary" onClick={() => setShowMovimento(true)}><ArrowUp size={16} /> Movimentação</button>
          <button className="btn btn-primary" onClick={() => { setEditando(null); setFormProd({ nome:'', tipo:'vacina', principio_ativo:'', fabricante:'', unidade:'ml', estoque_minimo:0, periodo_carencia_dias:0 }); setShowProduto(true); }}><Plus size={16} /> Novo Produto</button>
        </div>
      </div>

      {criticos.length > 0 && (
        <div style={{ background:'#FEF3C7', border:'1px solid #FDE68A', borderRadius:10, padding:'12px 16px', marginBottom:16, display:'flex', alignItems:'center', gap:10 }}>
          <AlertTriangle size={18} color="#D97706" />
          <div style={{ fontSize:14 }}><strong>Estoque crítico:</strong> {criticos.map(p => `${p.nome} (${fmt(p.estoque_atual)} ${p.unidade})`).join(' · ')}</div>
        </div>
      )}

      <div style={{ display:'flex', gap:6, marginBottom:16, justifyContent:'space-between', flexWrap:'wrap' }}>
        <div style={{ display:'flex', gap:6 }}>
          <button className={`btn ${tab==='produtos'?'btn-primary':'btn-secondary'}`} onClick={() => setTab('produtos')}>Produtos</button>
          <button className={`btn ${tab==='movimentos'?'btn-primary':'btn-secondary'}`} onClick={() => setTab('movimentos')}>Movimentações</button>
        </div>
        {tab === 'produtos' && (
          <div className="form-group" style={{ marginBottom:0, minWidth:200 }}>
            <select value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}>
              <option value="">Tipo de Produto: Todos</option>
              {TIPOS_PRODUTO.map(t => <option key={t} value={t}>{labelTipo(t)}</option>)}
            </select>
          </div>
        )}
      </div>

      {tab === 'produtos' && (
        loading ? <div className="card"><p>Carregando...</p></div>
          : produtosFiltrados.length === 0 ? <div className="card"><div className="empty-state"><p>Nenhum produto</p></div></div>
          : gruposPorTipo().map(grupo => (
            <div key={grupo.tipo} style={{ marginBottom:24 }}>
              {!filtroTipo && (
                <h3 style={{ fontSize:14, fontWeight:700, color:'var(--text-secondary)', textTransform:'uppercase', letterSpacing:0.5, marginBottom:10 }}>
                  {labelTipo(grupo.tipo)} <span style={{ fontWeight:400, textTransform:'none' }}>({grupo.itens.length})</span>
                </h3>
              )}
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(260px, 1fr))', gap:16 }}>
                {grupo.itens.map(p => {
                  const est = p.estoque_atual ?? 0;
                  const critico = est <= (p.estoque_minimo||0);
                  const pct = p.estoque_minimo > 0 ? Math.min(100, (est / (p.estoque_minimo * 3)) * 100) : (est > 0 ? 100 : 0);
                  return (
                    <div key={p.id} className="card" style={{ borderLeft:`4px solid ${critico ? 'var(--color-danger)' : TIPO_CORES[p.tipo]||'#666'}` }}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
                        <div>
                          <div style={{ fontWeight:700, fontSize:15 }}>{p.nome}</div>
                          <span className="badge badge-gray" style={{ fontSize:11, marginTop:2 }}>{labelTipo(p.tipo)}</span>
                        </div>
                        <div style={{ display:'flex', gap:4 }}>
                          <button className="btn btn-secondary btn-sm" onClick={() => abrir(p)}>Editar</button>
                          <button className="btn btn-secondary btn-sm" onClick={() => excluirProduto(p.id, p.nome)} style={{ color:'#DC2626', padding:'4px 6px' }}><Trash2 size={13} /></button>
                        </div>
                      </div>
                      {p.principio_ativo && <div style={{ fontSize:12, color:'var(--text-secondary)', marginBottom:6 }}>PA: {p.principio_ativo}</div>}
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
                        <span style={{ fontSize:22, fontWeight:700, color: critico ? 'var(--color-danger)' : 'var(--text-primary)' }}>{fmt(est)}</span>
                        <span style={{ fontSize:13, color:'var(--text-secondary)' }}>{p.unidade} · mín {fmt(p.estoque_minimo)}</span>
                      </div>
                      <div style={{ height:6, background:'var(--border-color)', borderRadius:3 }}>
                        <div style={{ height:'100%', width:`${pct}%`, background: critico ? 'var(--color-danger)' : 'var(--color-success)', borderRadius:3, transition:'width 0.3s' }} />
                      </div>
                      {critico && <div style={{ fontSize:12, color:'var(--color-danger)', marginTop:6, display:'flex', alignItems:'center', gap:4 }}><AlertTriangle size={12} /> Repor estoque</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          ))
      )}

      {tab === 'movimentos' && (
        <div className="card" style={{ padding:0, overflow:'hidden' }}>
          <table>
            <thead><tr><th>Produto</th><th>Tipo</th><th>Quantidade</th><th>Lote</th><th>Validade</th><th>Fornecedor</th><th>Data</th></tr></thead>
            <tbody>
              {movs.length === 0 ? <tr><td colSpan={7}><div className="empty-state"><p>Nenhuma movimentação</p></div></td></tr>
                : movs.map(m => (
                  <tr key={m.id}>
                    <td><strong>{m.produto_nome}</strong></td>
                    <td><span className={`badge ${m.tipo==='entrada'?'badge-green':m.tipo==='saida'?'badge-red':'badge-yellow'}`} style={{ display:'inline-flex', alignItems:'center', gap:4 }}>
                      {m.tipo==='entrada' ? <ArrowUp size={11}/> : <ArrowDown size={11}/>} {m.tipo}
                    </span></td>
                    <td><strong>{fmt(m.quantidade)} {m.unidade}</strong></td>
                    <td style={{ fontSize:13 }}>{m.lote_produto || '—'}</td>
                    <td style={{ fontSize:13, color: m.validade && new Date(m.validade) < new Date() ? 'var(--color-danger)' : 'inherit' }}>{m.validade ? formatDateBR(m.validade) : '—'}</td>
                    <td style={{ fontSize:13 }}>{m.fornecedor || '—'}</td>
                    <td style={{ fontSize:13, color:'var(--text-secondary)' }}>{new Date(m.created_at).toLocaleDateString('pt-BR')}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {showProduto && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && setShowProduto(false)}>
          <div className="modal">
            <div className="modal-header"><h2>{editando ? 'Editar Produto' : 'Novo Produto'}</h2><button className="btn btn-secondary btn-sm" onClick={() => setShowProduto(false)}>✕</button></div>
            <div className="modal-body">
              <div className="form-group"><label>Nome *</label><input value={formProd.nome} onChange={e => setFormProd(p => ({ ...p, nome:e.target.value }))} /></div>
              <div className="form-row">
                <div className="form-group"><label>Tipo *</label>
                  <select value={formProd.tipo} onChange={e => setFormProd(p => ({ ...p, tipo:e.target.value }))}>
                    {TIPOS_PRODUTO.map(t => <option key={t} value={t}>{labelTipo(t)}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>Unidade *</label>
                  <select value={formProd.unidade} onChange={e => setFormProd(p => ({ ...p, unidade:e.target.value }))}>
                    {UNIDADES.map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Princípio Ativo</label><input value={formProd.principio_ativo} onChange={e => setFormProd(p => ({ ...p, principio_ativo:e.target.value }))} /></div>
                <div className="form-group"><label>Fabricante</label><input value={formProd.fabricante} onChange={e => setFormProd(p => ({ ...p, fabricante:e.target.value }))} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Estoque Mínimo</label><input type="number" step="0.1" value={formProd.estoque_minimo} onChange={e => setFormProd(p => ({ ...p, estoque_minimo:e.target.value }))} /></div>
                <div className="form-group"><label>Carência (dias)</label><input type="number" value={formProd.periodo_carencia_dias} onChange={e => setFormProd(p => ({ ...p, periodo_carencia_dias:e.target.value }))} /></div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowProduto(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={salvarProduto}>Salvar</button>
            </div>
          </div>
        </div>
      )}

      {showMovimento && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && setShowMovimento(false)}>
          <div className="modal">
            <div className="modal-header"><h2>Movimentação de Estoque</h2><button className="btn btn-secondary btn-sm" onClick={() => setShowMovimento(false)}>✕</button></div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group"><label>Produto *</label>
                  <select value={formMov.produto_id} onChange={e => setFormMov(p => ({ ...p, produto_id:e.target.value }))}>
                    <option value="">Selecione</option>
                    {produtos.map(p => <option key={p.id} value={p.id}>{p.nome} ({fmt(p.estoque_atual)} {p.unidade})</option>)}
                  </select>
                </div>
                <div className="form-group"><label>Tipo *</label>
                  <select value={formMov.tipo} onChange={e => setFormMov(p => ({ ...p, tipo:e.target.value }))}>
                    <option value="entrada">Entrada (compra)</option><option value="saida">Saída</option><option value="ajuste">Ajuste</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Quantidade *</label><input type="number" step="0.1" value={formMov.quantidade} onChange={e => setFormMov(p => ({ ...p, quantidade:e.target.value }))} /></div>
                <div className="form-group"><label>Lote do produto</label><input value={formMov.lote_produto} onChange={e => setFormMov(p => ({ ...p, lote_produto:e.target.value }))} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Validade</label><input type="date" value={formMov.validade} onChange={e => setFormMov(p => ({ ...p, validade:e.target.value }))} /></div>
                <div className="form-group"><label>Custo Unitário</label><input type="number" step="0.01" value={formMov.custo_unitario} onChange={e => setFormMov(p => ({ ...p, custo_unitario:e.target.value }))} /></div>
              </div>
              <div className="form-group"><label>Fornecedor</label><input value={formMov.fornecedor} onChange={e => setFormMov(p => ({ ...p, fornecedor:e.target.value }))} /></div>
              <div className="form-group"><label>Nota Fiscal</label><input value={formMov.nota_fiscal} onChange={e => setFormMov(p => ({ ...p, nota_fiscal:e.target.value }))} /></div>
              <div className="form-group"><label>Motivo / Observação</label><textarea rows={2} value={formMov.motivo} onChange={e => setFormMov(p => ({ ...p, motivo:e.target.value }))} /></div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowMovimento(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={salvarMovimento} disabled={!formMov.produto_id || !formMov.quantidade}>Registrar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
